import React from 'react';

export type DynamicsChartLayoutVariant = 'inline' | 'expanded';

export interface DynamicsChartLayoutValue {
  variant: DynamicsChartLayoutVariant;
}

const defaultValue: DynamicsChartLayoutValue = { variant: 'inline' };

export const DynamicsChartLayoutContext =
  React.createContext<DynamicsChartLayoutValue>(defaultValue);

export function DynamicsChartLayoutProvider({
  value,
  children,
}: {
  value: DynamicsChartLayoutValue;
  children: React.ReactNode;
}) {
  return (
    <DynamicsChartLayoutContext.Provider value={value}>
      {children}
    </DynamicsChartLayoutContext.Provider>
  );
}

export function useDynamicsChartLayout(): DynamicsChartLayoutValue {
  return React.useContext(DynamicsChartLayoutContext);
}

/** Taller plot in expanded modal — matches viewport; base must match `ResponsiveContainer` height in inline mode. */
export function useDynamicsPlotHeight(baseHeight: number): number {
  const { variant } = useDynamicsChartLayout();
  const [expandedH, setExpandedH] = React.useState(() => computeExpandedPlotHeight(baseHeight));

  React.useEffect(() => {
    if (variant !== 'expanded') return;
    const sync = () => setExpandedH(computeExpandedPlotHeight(baseHeight));
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, [variant, baseHeight]);

  return variant === 'expanded' ? expandedH : baseHeight;
}

function computeExpandedPlotHeight(base: number): number {
  if (typeof window === 'undefined') return Math.min(900, Math.round(base * 1.5));
  const fromBase = Math.round(base * 1.32);
  const fromVh = Math.floor(window.innerHeight * 0.56);
  return Math.min(920, Math.max(fromBase, fromVh, 480));
}
